import { _ as __rest, a as __assign } from '../../tslib.es6-18cb2814.js';
import { c as classNames } from '../../index-8887fc24.js';
import React__default, { useCallback, cloneElement, useState, useEffect, useMemo, Fragment } from 'react';
import { s as skeletonStyles } from '../../skeleton.module-7c98b8f8.js';
import { D as DEFAULT_NUM_SKELETON_ROWS, a as DEFAULT_ROW_HEIGHT, b as DEFAULT_HEADER_HEIGHT } from '../../constants-6bdfc539.js';
import { s as styles$1 } from '../../DataTable.module-bf72b311.js';
import { N as NestingContext, T as TableCell } from '../../index-83fc3c9d.js';
import { T as TableHeader } from '../../index-34719664.js';
import { R as RowContext, u as useTableContext, C as ComposableTableContext } from '../../RowContext-011972fb.js';
import '../../ChevronRightIcon-b72559d2.js';
import '../../countries-34fea817.js';
import '../../index-177b9d11.js';
import '../../useId-f4885202.js';
import '../../CheckIcon-322b1ac3.js';
import './Tooltip.js';
import '../../index-b7606a36.js';
import 'react-spring';
import 'react/jsx-runtime';
import 'react-dom';
import '../../useOrchard-45cfc194.js';
import './IconButton.js';
import './TextLink.js';
import './Typography.js';
import '../../ResizeObserver-d78e3707.js';
import '../../CircleAlertIcon-a6cd83ad.js';
import '../../CloseIcon-25692563.js';
import '../../InfoIcon-22134611.js';
import '../../ThemeProvider-e3ab770b.js';

var Body = function (_a) {
    var children = _a.children, props = __rest(_a, ["children"]);
    return React__default.createElement("tbody", __assign({}, props), children);
};

var styles = {"row":"ComposableTable_row_f424d3bc","clickable":"ComposableTable_clickable_f424d3bc","depth1":"ComposableTable_depth1_f424d3bc","depth2":"ComposableTable_depth2_f424d3bc","depth3":"ComposableTable_depth3_f424d3bc","depth4":"ComposableTable_depth4_f424d3bc","depth5":"ComposableTable_depth5_f424d3bc","collapsed":"ComposableTable_collapsed_f424d3bc"};

var Head = function (_a) {
    var children = _a.children, rowId = _a.rowId, props = __rest(_a, ["children", "rowId"]);
    var renderChildren = useCallback(function () {
        if (!Array.isArray(children)) {
            return cloneElement(children, { isFirstCell: true });
        }
        return children.map(function (child, index) {
            if (!child)
                return undefined;
            var firstCell = index === 0;
            if (Array.isArray(child))
                return child.map(function (c, i) {
                    return cloneElement(c, { isFirstCell: firstCell, key: "".concat(index, "-").concat(i) });
                });
            return cloneElement(child, { isFirstCell: firstCell, key: index });
        });
    }, [children]);
    return (React__default.createElement(RowContext.Provider, { value: { rowId: rowId } },
        React__default.createElement("thead", __assign({}, props),
            React__default.createElement("tr", { className: styles.row }, children && renderChildren()))));
};

var Row = function (_a) {
    var children = _a.children, onClick = _a.onClick, className = _a.className, rowId = _a.rowId, isInitiallySelected = _a.isInitiallySelected, onRowStateChanged = _a.onRowStateChanged, collapsed = _a.collapsed, onCollapseChange = _a.onCollapseChange, __depth = _a.__depth, __collapsed = _a.__collapsed, name = _a.name, props = __rest(_a, ["children", "onClick", "className", "rowId", "isInitiallySelected", "onRowStateChanged", "collapsed", "onCollapseChange", "__depth", "__collapsed", "name"]);
    var _b = useTableContext(), isSelectable = _b.isSelectable, setInitialRowState = _b.setInitialRowState, registerHasNesting = _b.registerHasNesting, initialNestingOpen = _b.initialNestingOpen;
    var _c = useState(initialNestingOpen === undefined ? true : initialNestingOpen), isNestingOpen = _c[0], setIsNestingOpen = _c[1];
    useEffect(function () {
        if (isSelectable && setInitialRowState && rowId) {
            setInitialRowState(rowId, isInitiallySelected || false);
        }
        // Purposefully expluded setInitialRowState from deps as it was causing update loops
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isInitiallySelected, isSelectable, rowId]);
    /**
     * Filter the children to pull out rows vs cells, this allows us to
     * split where we render children to fit a table format and manage nesting
     */
    var filteredChildren = useMemo(function () {
        var childrenObj = { cells: [], rows: [] };
        var assignRowsOrCells = function (child) {
            var _a;
            // Cast to unknown then as any as React types are incorrect
            if (((_a = child === null || child === void 0 ? void 0 : child.type) === null || _a === void 0 ? void 0 : _a.orchardType) === "row")
                childrenObj.rows.push(child);
            else if (child)
                childrenObj.cells.push(child);
        };
        if (Array.isArray(children)) {
            children.forEach(function (c) {
                // Children can be a double nested array due to loop mapping
                if (Array.isArray(c)) {
                    c.forEach(function (i) { return assignRowsOrCells(i); });
                }
                else {
                    assignRowsOrCells(c);
                }
            });
        }
        else {
            assignRowsOrCells(children);
        }
        return childrenObj;
    }, [children]);
    useEffect(function () {
        if (filteredChildren.rows.length) {
            registerHasNesting && registerHasNesting();
        }
    });
    var resolvedIsNestingOpen = useMemo(function () {
        return collapsed !== undefined ? !collapsed : isNestingOpen;
    }, [collapsed, isNestingOpen]);
    var onRowClick = useCallback(function (e) {
        if (filteredChildren.rows.length) {
            onCollapseChange !== undefined
                ? onCollapseChange(!resolvedIsNestingOpen)
                : setIsNestingOpen(!resolvedIsNestingOpen);
            onRowStateChanged && onRowStateChanged(resolvedIsNestingOpen);
        }
        onClick && onClick(e);
    }, [
        filteredChildren.rows.length,
        onClick,
        onCollapseChange,
        onRowStateChanged,
        resolvedIsNestingOpen,
    ]);
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement("tr", __assign({}, props, { onClick: onRowClick, className: classNames(className, styles.row, (onClick || filteredChildren.rows.length) && styles.clickable, __depth && styles["depth".concat(__depth)]) }),
            React__default.createElement(RowContext.Provider, { value: { rowId: rowId, name: name } }, filteredChildren.rows.length ? (React__default.createElement(React__default.Fragment, null,
                React__default.createElement(NestingContext.Provider, { value: {
                        nested: true,
                        open: resolvedIsNestingOpen,
                        setOpen: onCollapseChange !== undefined
                            ? onCollapseChange
                            : setIsNestingOpen,
                    } }, filteredChildren.cells[0] &&
                    cloneElement(filteredChildren.cells[0], {
                        isFirstCell: true,
                    })),
                filteredChildren.cells.slice(1))) : (React__default.createElement(React__default.Fragment, null,
                filteredChildren.cells[0] &&
                    cloneElement(filteredChildren.cells[0], { isFirstCell: true }),
                filteredChildren.cells.slice(1))))),
        filteredChildren.rows.map(function (row, index) {
            var depth = __depth ? __depth + 1 : 1;
            return (React__default.createElement(Fragment, { key: index }, cloneElement(row, {
                __depth: depth,
                __collapsed: !resolvedIsNestingOpen || __collapsed,
                className: classNames(!resolvedIsNestingOpen || __collapsed
                    ? styles.collapsed
                    : undefined, row.props.className),
            })));
        })));
};
Row.orchardType = "row";

var ComposableTable = function (_a) {
    var _b, _c;
    var children = _a.children, caption = _a.caption, className = _a.className, sortKey = _a.sortKey, sortDirection = _a.sortDirection, onSort = _a.onSort, isLayoutAuto = _a.isLayoutAuto, isSkeleton = _a.isSkeleton, _d = _a.skeletonRows, skeletonRows = _d === void 0 ? DEFAULT_NUM_SKELETON_ROWS : _d, _e = _a.skeletonRowHeight, skeletonRowHeight = _e === void 0 ? DEFAULT_ROW_HEIGHT : _e, _f = _a.skeletonHeaderHeight, skeletonHeaderHeight = _f === void 0 ? DEFAULT_HEADER_HEIGHT : _f, isSelectable = _a.isSelectable, maxSelectionCount = _a.maxSelectionCount, onSelectionChanged = _a.onSelectionChanged, onClearSelection = _a.onClearSelection, onSelectAll = _a.onSelectAll, selection = _a.selection, initialSelection = _a.initialSelection, _g = _a.initialNestingOpen, initialNestingOpen = _g === void 0 ? true : _g, onRowSelectedChanged = _a.onRowSelectedChanged, isSelectionDisabled = _a.isSelectionDisabled, canSelectAll = _a.canSelectAll, props = __rest(_a, ["children", "caption", "className", "sortKey", "sortDirection", "onSort", "isLayoutAuto", "isSkeleton", "skeletonRows", "skeletonRowHeight", "skeletonHeaderHeight", "isSelectable", "maxSelectionCount", "onSelectionChanged", "onClearSelection", "onSelectAll", "selection", "initialSelection", "initialNestingOpen", "onRowSelectedChanged", "isSelectionDisabled", "canSelectAll"]);
    var _h = useState(initialSelection || {}), rowState = _h[0], setRowState = _h[1];
    var _j = useState(false), hasNesting = _j[0], setHasNesting = _j[1];
    useEffect(function () {
        if (selection &&
            !Object.entries(selection).every(function (s) { return rowState[s[0]] === s[1]; }))
            setRowState(selection);
    }, [rowState, selection]);
    var registerHasNesting = function () {
        setHasNesting(true);
    };
    var onRowSelect = useCallback(function (rowId, name) {
        var mutable = __assign({}, rowState);
        var currentCount = Object.values(rowState).filter(function (v) { return v; }).length;
        if (mutable[rowId] === true) {
            mutable[rowId] = false;
            !selection && setRowState(mutable);
            onRowSelectedChanged && onRowSelectedChanged(rowId, false, name);
        }
        else if (maxSelectionCount && currentCount >= maxSelectionCount)
            return;
        else {
            mutable[rowId] = !mutable[rowId];
            !selection && setRowState(mutable);
            onRowSelectedChanged &&
                onRowSelectedChanged(rowId, !!mutable[rowId], name);
        }
    }, [maxSelectionCount, onRowSelectedChanged, rowState, selection]);
    var setInitialRowState = useCallback(function (rowId, checked) {
        setRowState(function (prev) {
            var mutable = __assign({}, prev);
            mutable[rowId] = initialSelection ? initialSelection[rowId] : checked;
            return mutable;
        });
    }, [initialSelection]);
    var clearRowState = function () {
        if (!selection)
            setRowState(function (prev) {
                return Object.fromEntries(Object.keys(prev).map(function (key) { return [key, false]; }));
            });
        onClearSelection === null || onClearSelection === void 0 ? void 0 : onClearSelection();
    };
    var selectAll = function () {
        if (!selection)
            setRowState(function (prev) {
                return Object.fromEntries(Object.keys(prev).map(function (key) { return [key, true]; }));
            });
        onSelectAll === null || onSelectAll === void 0 ? void 0 : onSelectAll();
    };
    useEffect(function () {
        onSelectionChanged && onSelectionChanged(rowState);
    }, [onSelectionChanged, rowState]);
    var skeletonStyle = {
        height: skeletonRows * skeletonRowHeight + skeletonHeaderHeight,
    };
    return (React__default.createElement("div", { "aria-busy": isSkeleton, className: classNames(styles$1.container, className, (_b = {},
            _b[skeletonStyles.skeleton] = isSkeleton,
            _b[styles$1.skeleton] = isSkeleton,
            _b)), style: isSkeleton ? skeletonStyle : undefined },
        React__default.createElement(ComposableTableContext.Provider, { value: {
                id: props.id,
                sortKey: sortKey,
                sortDirection: sortDirection,
                onSort: onSort,
                isSelectable: isSelectable,
                rowState: rowState,
                onRowSelect: onRowSelect,
                setInitialRowState: setInitialRowState,
                clearRowState: clearRowState,
                maxSelectionCount: maxSelectionCount,
                hasNesting: hasNesting,
                registerHasNesting: registerHasNesting,
                initialNestingOpen: initialNestingOpen,
                isSelectionDisabled: isSelectionDisabled,
                canSelectAll: canSelectAll,
                selectAll: selectAll,
            } },
            React__default.createElement("table", __assign({}, props, { className: classNames(styles$1.table, (_c = {},
                    _c[styles$1.layoutAuto] = isLayoutAuto,
                    _c)) }),
                caption && (React__default.createElement("caption", { className: styles$1.visuallyHidden }, caption)),
                children))));
};
ComposableTable.Head = Head;
ComposableTable.Header = TableHeader;
ComposableTable.Row = Row;
ComposableTable.Body = Body;
ComposableTable.Cell = TableCell;

export { ComposableTable };
